import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IBulkWasteReceiptPatch,
  IUpsertWasteReceiptDraft,
  IWasteReceiptGenerateResult,
  IWasteReceiptRow,
  IWasteReceiptSubmitResult,
} from "../models/wasteTracking/IWasteReceiptRow";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

export const WASTE_TRACKING_TABLE_URL = "/wastereceipt";

/**
 * The yard's consignment note photo, as a Blob. Only fetched while a documentId is given (the
 * review modal is open), and kept so re-opening the same note doesn't download it again.
 */
export function useConsignmentNotePhoto(documentId: string | null | undefined, componentName = "useConsignmentNotePhoto") {
  const { getFile } = useApiHelper(apiConfig(componentName));

  return useQuery<Blob>(["waste-tracking", "consignment-note-photo", documentId], () => getFile<Blob>(`/vehicle/documents/${documentId}`), {
    enabled: !!documentId,
    staleTime: Infinity,
  });
}

export default function useWasteTracking(componentName = "useWasteTracking") {
  const { get, post, put } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  // The page is a PagedTable over /wastereceipt/{view}, so every write refreshes those queries.
  const invalidate = () => {
    queryClient.invalidateQueries(["pagedTable", `${WASTE_TRACKING_TABLE_URL}/pending`]);
    queryClient.invalidateQueries(["pagedTable", `${WASTE_TRACKING_TABLE_URL}/submitted`]);
    queryClient.invalidateQueries(["pagedTable", `${WASTE_TRACKING_TABLE_URL}/excluded`]);
    queryClient.invalidateQueries(["waste-tracking"]);
  };

  const getReadyIds = (startDate?: string | null, endDate?: string | null) => {
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    const query = params.toString();
    return get<DefaultResponseWithData<string[]>>(
      `${WASTE_TRACKING_TABLE_URL}/pending/ready-ids${query ? `?${query}` : ""}`
    );
  };

  const upsertDraft = useMutation(
    ({ vehicleId, draft }: { vehicleId: string; draft: IUpsertWasteReceiptDraft }) =>
      put<IUpsertWasteReceiptDraft, DefaultResponseWithData<IWasteReceiptRow>>(
        `${WASTE_TRACKING_TABLE_URL}/draft/${vehicleId}`,
        draft
      ),
    { onSuccess: invalidate }
  );

  const bulkUpdate = useMutation(
    (body: IBulkWasteReceiptPatch) =>
      post<IBulkWasteReceiptPatch, DefaultResponseWithData<number>>(`${WASTE_TRACKING_TABLE_URL}/bulk/update`, body),
    { onSuccess: invalidate }
  );

  const exclude = useMutation(
    (vehicleIds: string[]) =>
      post<{ vehicleIds: string[] }, DefaultResponseWithData<number>>(`${WASTE_TRACKING_TABLE_URL}/bulk/exclude`, { vehicleIds }),
    { onSuccess: invalidate }
  );

  const restore = useMutation(
    (vehicleIds: string[]) =>
      post<{ vehicleIds: string[] }, DefaultResponseWithData<number>>(`${WASTE_TRACKING_TABLE_URL}/bulk/restore`, { vehicleIds }),
    { onSuccess: invalidate }
  );

  // One chunk (max 25 ids) per call; the page loops over chunks so a big run can't time out.
  const submit = useMutation(
    (vehicleIds: string[]) =>
      post<{ vehicleIds: string[] }, DefaultResponseWithData<IWasteReceiptSubmitResult[]>>(
        `${WASTE_TRACKING_TABLE_URL}/submit`,
        { vehicleIds }
      ),
    { onSuccess: invalidate }
  );

  const amend = useMutation(
    (vehicleId: string) =>
      post<null, DefaultResponseWithData<IWasteReceiptSubmitResult>>(`${WASTE_TRACKING_TABLE_URL}/${vehicleId}/amend`, null),
    { onSuccess: invalidate }
  );

  // Next code in the site's own sequence (DEFRA:ConsignmentPremisesCode / ConsignmentNoteNextNumber).
  // The API refuses to overwrite an existing code unless replace is set.
  const generateCode = useMutation(
    ({ vehicleId, replace = false }: { vehicleId: string; replace?: boolean }) =>
      post<null, DefaultResponseWithData<IWasteReceiptRow>>(
        `${WASTE_TRACKING_TABLE_URL}/${vehicleId}/consignment-code/generate?replace=${replace}`,
        null
      ),
    { onSuccess: invalidate }
  );

  // One code each for the selected vehicles without one; vehicles that have a code are skipped.
  const bulkGenerateCodes = useMutation(
    (vehicleIds: string[]) =>
      post<{ vehicleIds: string[] }, DefaultResponseWithData<IWasteReceiptGenerateResult[]>>(
        `${WASTE_TRACKING_TABLE_URL}/bulk/generate-consignment-codes`,
        { vehicleIds }
      ),
    { onSuccess: invalidate }
  );

  // The office has checked the AI's read of the yard's consignment note photo; lifts the hold on the row.
  const confirmScan = useMutation(
    (vehicleId: string) =>
      post<null, DefaultResponseWithData<IWasteReceiptRow>>(`${WASTE_TRACKING_TABLE_URL}/${vehicleId}/consignment-note/confirm`, null),
    { onSuccess: invalidate }
  );

  // Runs the AI over the photo already on file again. Same blank-only rules as the first read.
  const rescan = useMutation(
    (vehicleId: string) =>
      post<null, DefaultResponseWithData<IWasteReceiptRow>>(`${WASTE_TRACKING_TABLE_URL}/${vehicleId}/consignment-note/rescan`, null),
    { onSuccess: invalidate }
  );

  return {
    getReadyIds,
    upsertDraft,
    bulkUpdate,
    exclude,
    restore,
    submit,
    amend,
    generateCode,
    bulkGenerateCodes,
    confirmScan,
    rescan,
    invalidate,
  };
}
